<script>
import { useVuelidate } from '@vuelidate/core';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import IconNext from 'next/icon/Icon.vue';
import Checkbox from 'next/wgpt/Form/CheckBox.vue';

export default {
  components: {
    Checkbox,
    IconNext,
  },
  props: {
    value: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    defaultValue: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    label: {
      type: String,
      default: '',
    },
    optionLabel: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    fallbackLabel: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      v$: useVuelidate(),
      toLocale,
    };
  },
  data() {
    return {
      updatedValue: this.value ?? this.defaultValue,
    };
  },
  computed: {
    descriptionTooltip() {
      if (!this.description) return null;

      return {
        content: this.toLocale(this.description),
        popperClass:
          'max-w-[300px] max-h-[300px] overflow-y-auto whitespace-pre-line',
        boundary: 'body',
        popperTriggers: ['hover'],
      };
    },
  },
  validations() {
    return {
      updatedValue: {
        customValidator(value) {
          if (this.required === undefined) {
            return true;
          }
          if (this.required === true) {
            return value === true;
          }
          return true;
        },
      },
    };
  },
  watch: {
    value(value) {
      this.updatedValue = value;
    },
  },
  mounted() {
    if (this.dirty) {
      this.$nextTick(() => {
        this.v$.$validate();
      });
    }
  },
  methods: {
    updateValue() {
      this.v$.$touch();
      this.$emit('change', this.updatedValue);
    },
  },
};
</script>

<template>
  <div
    class="relative flex flex-col min-w-0 gap-1"
    :class="{ 'checkbox-error': v$.updatedValue.$error }"
  >
    <label
      v-if="label || descriptionTooltip"
      class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
    >
      <label v-if="label" class="block truncate">
        {{ toLocale(label, fallbackLabel) }}<span v-if="required">*</span>
      </label>
      <IconNext
        v-if="descriptionTooltip"
        v-tooltip.top="descriptionTooltip"
        icon="i-lucide-info"
        class="text-n-slate-11 size-3.5"
      />
    </label>

    <div class="flex items-center min-h-10">
      <Checkbox
        v-model="updatedValue"
        class="me-2 !mb-0"
        :disabled="disabled"
        @change="updateValue"
      />
      <span v-if="optionLabel" class="truncate">{{
        toLocale(optionLabel)
      }}</span>
    </div>

    <span v-if="v$.updatedValue.$error" class="message">
      <template v-if="v$.updatedValue.customValidator.$invalid">{{
        $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.REQUIRED')
      }}</template>
    </span>
  </div>
</template>

<style lang="scss" scoped>
.button--only-icon ::v-deep * {
  @apply pointer-events-none;
}

.checkbox-error input {
  @apply border-n-ruby-5;
}
</style>
